// Recurring invoices — rebuilt to the invoiceTest recurring.js layout (parity S10) over the wave
// recurring backend (documents.recurring_profile, recurring_service.py, routes/recurring.py —
// on-demand run-due, no cron). iT structure: "Automation / Repeating invoices" head + Generate due
// now, iT table.list with the marquee INLINE CADENCE combobox on each row (commitOnBlur:false —
// onSelect re-renders, so a sync blur-commit would destroy a button mid-click; iT bug review
// 2026-08-07 Finding 1), Active/Paused iT pills, and armed Confirm/Cancel deletes. Schema kept:
// standalone create/edit (iT profiles only come from "Make recurring" on a document), every-N
// schedules (an every>1 profile shows its composite cadence; picking Weekly/Monthly resets to 1),
// run-now per row, est-total/type/lines columns, server pagination + search. The modal's contact +
// line account/tax pickers are searchSelect comboboxes (the iT editor precedent); doc-type / unit /
// on-each-run stay native (small schema enums, like iT's bp-method).
import { html, signal } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { tenantShell } from './tshell.js';
import { fmtR, formatDate } from './bank_helpers.js';
import { t } from '../shared/i18n.js';
import { pager, perPageSelect, DEFAULT_PER_PAGE } from '../shared/pager.js';
import { searchSelect } from '../shared/searchselect.js';
import { fmtRate } from '../shared/formats.js';
import { configureSleekDate } from '../shared/sleekdate.js';
import '../shared/sleekdate.js';   // registers <sleek-date>

const DOC_LABELS = { INV: 'Customer invoice', SINV: 'Supplier invoice' };
const ACTION_LABELS = {
  draft: 'Save as draft', confirm: 'Confirm (post)', confirm_send: 'Confirm & email',
};
// iT pill palette: Active=pill-due, Paused/Ended=pill-void.
const STATUS_PILL = { active: 'pill-due', paused: 'pill-void', ended: 'pill-void' };

export function recurringScreen(companyId){
  const rows = signal([]), total = signal(0), page = signal(1), pages = signal(1),
        perPage = signal(DEFAULT_PER_PAGE), loading = signal(true), notice = signal(''),
        busy = signal(''), reference = signal({});
  let timer, lastQ = '';

  const dmy = iso => formatDate(iso, (reference.value || {}).date_format);
  function toast(m){ notice.value = m; setTimeout(() => { if(notice.value === m) notice.value = ''; }, 3500); }

  async function load(){
    loading.value = true;
    const p = new URLSearchParams({ per_page: perPage.value, page: String(page.value) });
    if(lastQ.trim()) p.set('q', lastQ.trim());
    try {
      const r = await api.get('/companies/' + companyId + '/recurring-profiles?' + p);
      rows.value = r.data || []; total.value = r.total || 0; pages.value = r.total_pages || 1;
    } catch(e){ rows.value = []; toast((e.data && e.data.error) || t('recurring.load_failed', 'Could not load recurring profiles')); }
    loading.value = false;
  }
  // The document editor's reference (contacts / accounts / tax codes) feeds the create/edit modal.
  api.get('/companies/' + companyId + '/documents-reference')
    .then(r => {
      reference.value = r || {};
      configureSleekDate({ date_format: reference.value.date_format, periods: reference.value.periods,
        fy_start_day: reference.value.fy_start_day, fy_start_month: reference.value.fy_start_month });
    }).catch(() => {});
  load();

  function onSearch(e){ lastQ = e.target.value; page.value = 1; clearTimeout(timer); timer = setTimeout(load, 220); }
  function onPer(e){ perPage.value = e.target.value; page.value = 1; load(); }
  function goPage(p){ page.value = p; load(); }

  async function act(id, verb, past){
    busy.value = id + ':' + verb;
    try {
      const r = await api.post('/companies/' + companyId + '/recurring-profiles/' + id + '/' + verb, {});
      if(r && r.document_no) toast(t('recurring.ran_made', 'Created') + ' ' + r.document_no);
      else toast(past);
      await load();
    } catch(e){ toast((e.data && e.data.error) || t('recurring.action_failed', 'Action failed')); }
    busy.value = '';
  }
  const runNow = id => act(id, 'run-now', t('recurring.ran', 'Ran now'));
  const pause  = id => act(id, 'pause', t('recurring.paused', 'Paused'));
  const resume = id => act(id, 'resume', t('recurring.resumed', 'Resumed'));

  async function generateDue(){
    busy.value = 'run-due';
    try {
      const r = await api.post('/companies/' + companyId + '/recurring/run-due', {});
      const n = (r && r.fired && r.fired.length) || 0;
      toast(n ? (t('recurring.generated', 'Generated') + ' ' + n + ' ' + (n === 1 ? t('recurring.document', 'document') : t('recurring.documents', 'documents')))
              : t('recurring.none_due', 'Nothing was due'));
      await load();
    } catch(e){ toast((e.data && e.data.error) || t('recurring.run_due_failed', 'Could not generate due documents')); }
    busy.value = '';
  }

  // ── delete (armed Confirm/Cancel — iT) ──
  const confirmId = signal(null), deleting = signal(false), delErr = signal('');
  function armDelete(id){ delErr.value = ''; confirmId.value = id; }
  function cancelDelete(){ confirmId.value = null; delErr.value = ''; }
  async function doDelete(id){
    deleting.value = true; delErr.value = '';
    try { await api.del('/companies/' + companyId + '/recurring-profiles/' + id); confirmId.value = null; await load(); toast(t('recurring.deleted', 'Profile deleted')); }
    catch(e){ delErr.value = (e.data && e.data.error) || e.message || t('recurring.delete_failed', 'Delete failed'); }
    deleting.value = false;
  }

  async function openEdit(id){
    let existing = null;
    if(id){
      try {
        const r = await api.get('/companies/' + companyId + '/recurring-profiles/' + id);
        existing = (r && r.profile) || r;   // the API nests the profile in {profile, runs…}
      }
      catch(e){ toast((e.data && e.data.error) || t('recurring.load_one_failed', 'Could not load the profile')); return; }
    }
    openProfileModal(companyId, reference.value, existing, load, toast);
  }

  // Inline cadence change (iT's marquee interaction): pick Weekly / Monthly straight in the row.
  // The wave schedule is every-N, so the PUT resets to every 1 of the picked unit; an every>1
  // profile shows its composite schedule as the current (read-only) option until changed via Edit.
  async function setCadence(id, unit){
    busy.value = id + ':cadence';
    try {
      const r = await api.get('/companies/' + companyId + '/recurring-profiles/' + id);
      const ex = (r && r.profile) || r;    // nested {profile} envelope (same as openEdit)
      await api.put('/companies/' + companyId + '/recurring-profiles/' + id, {
        reference: ex.reference, doc_type: ex.doc_type, contact_id: ex.contact_id,
        schedule_every: 1, schedule_unit: unit,
        start_date: ex.start_date, end_date: ex.end_date || null,
        action: ex.action, notes: ex.notes || null, lines: ex.lines || [] });
      toast(t('recurring.cadence_saved', 'Cadence updated'));
      await load();
    } catch(e){ toast((e.data && e.data.error) || t('recurring.save_failed', 'Could not save the profile')); }
    busy.value = '';
  }

  // The list is IMPERATIVE (iT): built into a host once per data change, so the per-row cadence
  // searchSelects can be mounted into their cells without fighting the reactive renderer.
  const listHost = document.createElement('div');
  function renderList(){
    listHost.replaceChildren(listView());
  }
  function listView(){
    if(loading.value){ const d = document.createElement('div'); d.className = 'muted-block'; d.textContent = t('common.loading', 'Loading…'); return d; }
    const data = rows.value;
    if(!data.length){ const d = document.createElement('div'); d.className = 'empty'; d.textContent = t('recurring.empty', 'No recurring profiles yet — create one, or open an invoice and click ↻ Make recurring.'); return d; }
    const bz = busy.value, cId = confirmId.value, dErr = delErr.value, del = deleting.value;
    const pg = () => pager({ page: page.value, totalPages: pages.value, total: total.value,
                             perPage: Number(perPage.value), onPage: goPage });
    const frag = html`<div class="list-wrap">${pg()}
      <div class="tbl-wrap"><table class="list rec-tbl">
        <thead><tr>
          <th>${t('recurring.col_reference', 'Reference')}</th>
          <th>${t('recurring.col_contact', 'Contact')}</th>
          <th>${t('recurring.col_type', 'Type')}</th>
          <th>${t('recurring.col_cadence', 'Cadence')}</th>
          <th>${t('recurring.col_next', 'Next date')}</th>
          <th class="ta-r">${t('recurring.col_generated', 'Generated')}</th>
          <th class="ta-r">${t('recurring.col_est', 'Est. total')}</th>
          <th>${t('recurring.col_status', 'Status')}</th>
          <th class="ta-r">${t('common.actions', 'Actions')}</th>
        </tr></thead>
        <tbody>${data.map(r => html`<tr class=${'rec-row rec-' + r.status}>
          <td><b>${r.reference}</b>${r.notes ? html`<div class="cell-sub">${r.notes}</div>` : ''}</td>
          <td>${r.contact_name || '—'}</td>
          <td><span class="rec-type">${DOC_LABELS[r.doc_type] || r.doc_type}</span></td>
          <td data-cadence-cell=${r.id}><div class="cell-sub">${r.line_count} ${r.line_count === 1 ? t('recurring.line', 'line') : t('recurring.lines', 'lines')}</div></td>
          <td class="mono">${dmy(r.next_run_date)}</td>
          <td class="ta-r num">${r.generated_count || 0}</td>
          <td class="ta-r num">${fmtR(r.est_total)}</td>
          <td><span class=${'pill ' + (STATUS_PILL[r.status] || 'pill-void')}>${r.status === 'active' ? t('recurring.active', 'Active') : r.status === 'paused' ? t('recurring.paused_lbl', 'Paused') : t('recurring.ended', 'Ended')}</span></td>
          <td class="ta-r nowrap rec-actions">
            ${cId === r.id
              ? html`<button type="button" class="btn sm bad" ?disabled=${del} @click=${() => doDelete(r.id)}>${del ? '…' : t('doc.confirm', 'Confirm')}</button><button type="button" class="btn sm" @click=${cancelDelete}>${t('common.cancel', 'Cancel')}</button>${dErr ? html`<div class="row-err">${dErr}</div>` : ''}`
              : html`
                ${r.status !== 'ended' ? html`<button type="button" class="btn sm" ?disabled=${bz === r.id + ':run-now'} @click=${() => runNow(r.id)}>${bz === r.id + ':run-now' ? '…' : t('recurring.run_now', 'Run now')}</button>` : ''}
                ${r.status === 'active' ? html`<button type="button" class="btn sm" ?disabled=${bz === r.id + ':pause'} @click=${() => pause(r.id)}>${t('recurring.pause', 'Pause')}</button>` : ''}
                ${r.status === 'paused' ? html`<button type="button" class="btn sm" ?disabled=${bz === r.id + ':resume'} @click=${() => resume(r.id)}>${t('recurring.resume', 'Resume')}</button>` : ''}
                <button type="button" class="icon-btn" title=${t('common.edit', 'Edit')} @click=${() => openEdit(r.id)}>✎</button>
                <button type="button" class="btn sm bad" @click=${() => armDelete(r.id)}>${t('common.delete', 'Delete')}</button>`}
          </td></tr>`)}</tbody>
      </table></div>
      ${pg()}
      <div class="hint rec-hint">${t('recurring.hint', 'Profiles fire when "Generate due now" runs — or the server-side runner on its schedule.')}</div>
    </div>`;
    // Mount the inline cadence comboboxes (iT: pick-from-list only — commitOnBlur:false).
    data.forEach(r => {
      const cell = frag.querySelector(`[data-cadence-cell="${r.id}"]`);
      if(!cell) return;
      const every = Number(r.schedule_every) || 1;
      const base = [{ value: 'week', label: t('recurring.weekly', 'Weekly') }, { value: 'month', label: t('recurring.monthly', 'Monthly') }];
      const options = every > 1 ? [{ value: '__custom', label: r.schedule }, ...base] : base;
      const sel = searchSelect({
        options, value: every > 1 ? '__custom' : (r.schedule_unit || 'month'),
        ariaLabel: t('recurring.col_cadence', 'Cadence'), commitOnBlur: false,
        onSelect: v => { if(v !== '__custom') setCadence(r.id, v); },
      });
      cell.prepend(sel.el);
    });
    return frag;
  }
  // Re-render the imperative list whenever its inputs change (one effect-like reactive slot that
  // renders NOTHING — it just watches; the host node itself lives in the static template below).
  const listWatch = () => {
    void loading.value; void rows.value; void busy.value;
    void confirmId.value; void deleting.value; void delErr.value;
    renderList();
    return '';
  };

  const content = html`<div class="page page-wide">
    <div class="page-head">
      <div><span class="eyebrow">${() => t('recurring.eyebrow', 'Automation')}</span><h1>${() => t('nav.recurring', 'Repeating invoices')}</h1></div>
      <div class="row-actions">
        <button type="button" class="btn primary" data-run-due ?disabled=${() => busy.value === 'run-due'} @click=${generateDue}>${() => busy.value === 'run-due' ? t('recurring.generating', 'Generating…') : t('recurring.generate_due', 'Generate due now')}</button>
        <button type="button" class="btn" data-new-profile @click=${() => openEdit(null)}>${t('recurring.new', 'New profile')}</button>
      </div>
    </div>
    ${() => notice.value ? html`<div class="notice">${notice.value}</div>` : ''}
    <div class="list-toolbar">
      <input class="search ux-search" type="search" data-search @input=${onSearch} placeholder=${t('recurring.search_ph', 'e.g. Monthly retainer')} aria-label=${t('recurring.search_aria', 'Search profiles')}>
      ${perPageSelect(perPage.value, onPer)}
    </div>
    ${listHost}
    ${listWatch}
  </div>`;

  return tenantShell(companyId, t('nav.recurring', 'Repeating invoices'), content);
}

// ── Create/Edit modal (plain-DOM, focus-safe) ────────────────────────────────
// Inputs are reached by data-* querySelector and read at submit — never signal-bound — so the
// lines editor (add/remove rows, live recompute) never triggers the tina4 focus trap.
export function openProfileModal(companyId, reference, existing, onSaved, toast){
  const contacts = (reference && reference.contacts) || [];
  const accounts = (reference && reference.accounts) || [];
  const taxes = (reference && reference.tax_codes) || [];
  const ex = existing || {};
  const exLines = (ex.lines && ex.lines.length) ? ex.lines : [{ description: '', qty: 1, unit_price: 0, discount_pc: 0 }];
  const todayIso = (d => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'))(new Date());

  // searchSelect option sets — the line account/tax + contact pickers are comboboxes (iT editor
  // precedent); '' rows make Default / None explicitly selectable.
  const acctSsOptions = [{ value: '', label: t('recurring.default_account', '— Default —') },
    ...accounts.map(a => ({ value: String(a.id), label: (a.code ? a.code + ' · ' : '') + a.name, keywords: a.name }))];
  const taxSsOptions = [{ value: '', label: t('recurring.no_tax', '— None —') },
    ...taxes.map(x => ({ value: String(x.id), label: (x.name || x.code) + (x.rate != null ? ' (' + fmtRate(x.rate) + '%)' : ''), keywords: x.code }))];

  function lineRowNode(ln){
    ln = ln || {};
    const node = html`<tr class="rec-line">
      <td><input class="form-control" data-l-desc placeholder="e.g. Monthly retainer" value=${ln.description || ''}></td>
      <td><input class="form-control r" type="number" step="0.01" data-l-qty placeholder="e.g. 1" value=${ln.qty != null ? ln.qty : 1}></td>
      <td><input class="form-control r" type="number" step="0.01" data-l-price placeholder="e.g. 2500.00" value=${ln.unit_price != null ? ln.unit_price : ''}></td>
      <td><input class="form-control r" type="number" step="0.01" data-l-disc placeholder="0" value=${ln.discount_pc != null ? ln.discount_pc : 0}></td>
      <td data-l-acct-cell></td>
      <td data-l-tax-cell></td>
      <td class="r"><button type="button" class="btn sm" data-l-remove title=${t('recurring.remove_line', 'Remove line')}>✕</button></td>
    </tr>`;
    // node is a fragment — grab the <tr> before it is appended (append empties the fragment).
    const tr = node.querySelector('tr');   // this line's own fragment (not the modal overlay)
    tr._acctSel = searchSelect({ options: acctSsOptions,
      value: ln.account_id != null && ln.account_id !== '' ? String(ln.account_id) : '',
      ariaLabel: t('doc.account', 'Account'), placeholder: 'e.g. 0400 · Sales' });
    tr.querySelector('[data-l-acct-cell]').append(tr._acctSel.el);
    tr._taxSel = searchSelect({ options: taxSsOptions,
      value: ln.tax_code_id != null && ln.tax_code_id !== '' ? String(ln.tax_code_id) : '',
      ariaLabel: t('doc.tax', 'Tax'), placeholder: 'e.g. 01 (15%)' });
    tr.querySelector('[data-l-tax-cell]').append(tr._taxSel.el);
    tr.querySelector('[data-l-remove]').addEventListener('click', () => { tr.remove(); recompute(); });
    ['[data-l-qty]', '[data-l-price]', '[data-l-disc]'].forEach(s => tr.querySelector(s).addEventListener('input', recompute));
    return tr;
  }

  const node = html`<div class="modal-overlay">
    <div class="modal-card modal-wide rec-modal">
      <div class="modal-head"><h3>${ex.id ? t('recurring.edit_title', 'Edit recurring profile') : t('recurring.new_title', 'New recurring profile')}</h3>
        <button type="button" class="modal-x" data-cancel>✕</button></div>
      <div class="rec-grid">
        <label class="pay-f rec-f-wide"><span>${t('recurring.reference', 'Reference (name)')}</span>
          <input class="form-control" data-reference placeholder="e.g. Monthly retainer — Acme" value=${ex.reference || ''}></label>
        <label class="pay-f"><span>${t('recurring.doc_type', 'Document type')}</span>
          <select class="form-control" data-doc-type>
            ${Object.keys(DOC_LABELS).map(k => html`<option value=${k} ?selected=${k === (ex.doc_type || 'INV')}>${DOC_LABELS[k]}</option>`)}</select></label>
        <label class="pay-f"><span>${t('recurring.contact', 'Contact')}</span>
          <span data-contact-cell></span></label>
        <label class="pay-f"><span>${t('recurring.every', 'Repeat every')}</span>
          <input class="form-control" type="number" min="1" step="1" data-every value=${ex.schedule_every || 1}></label>
        <label class="pay-f"><span>${t('recurring.unit', 'Unit')}</span>
          <select class="form-control" data-unit>
            <option value="week" ?selected=${ex.schedule_unit === 'week'}>${t('recurring.weeks', 'week(s)')}</option>
            <option value="month" ?selected=${(ex.schedule_unit || 'month') === 'month'}>${t('recurring.months', 'month(s)')}</option></select></label>
        <label class="pay-f"><span>${t('recurring.start', 'Start date')}</span>
          <sleek-date class="form-control" data-start data-free="true" .value=${ex.start_date || todayIso}></sleek-date></label>
        <label class="pay-f"><span>${t('recurring.end', 'End date (optional)')}</span>
          <sleek-date class="form-control" data-end data-free="true" .value=${ex.end_date || ''}></sleek-date></label>
        <label class="pay-f"><span>${t('recurring.action', 'On each run')}</span>
          <select class="form-control" data-action>
            ${Object.keys(ACTION_LABELS).map(k => html`<option value=${k} ?selected=${k === (ex.action || 'draft')}>${ACTION_LABELS[k]}</option>`)}</select></label>
        <label class="pay-f rec-f-wide"><span>${t('recurring.notes', 'Notes (optional)')}</span>
          <input class="form-control" data-notes placeholder="e.g. Retainer per the service agreement" value=${ex.notes || ''}></label>
      </div>
      <h4 class="rec-lines-h">${t('recurring.lines_h', 'Lines')}</h4>
      <table class="doc-lines-tbl rec-lines-tbl">
        <thead><tr>
          <th>${t('doc.description', 'Description')}</th><th class="r">${t('doc.qty', 'Qty')}</th>
          <th class="r">${t('doc.unit_price', 'Unit price')}</th><th class="r">${t('doc.discount', 'Disc %')}</th>
          <th>${t('doc.account', 'Account')}</th><th>${t('doc.tax', 'Tax')}</th><th></th>
        </tr></thead>
        <tbody data-lines></tbody>
      </table>
      <div class="rec-lines-foot">
        <button type="button" class="btn btn-sm btn-outline" data-add-line>+ ${t('recurring.add_line', 'Add line')}</button>
        <span class="rec-est">${t('recurring.est_net', 'Est. net')}: <b data-est>—</b></span>
      </div>
      <div class="row-err" data-err hidden></div>
      <div class="pay-actions">
        <button type="button" class="btn btn-outline" data-cancel2>${t('common.cancel', 'Cancel')}</button>
        <button type="button" class="btn btn-primary" data-go>${ex.id ? t('common.save', 'Save') : t('recurring.create', 'Create profile')}</button>
      </div>
    </div></div>`;

  const overlay = node.querySelector('.modal-overlay');
  const linesBody = overlay.querySelector('[data-lines]');
  const estEl = overlay.querySelector('[data-est]');
  const errEl = overlay.querySelector('[data-err]');
  const goEl = overlay.querySelector('[data-go]');
  // Seed <select>s imperatively (don't trust ?selected on a one-time render — tina4 gotcha).
  overlay.querySelector('[data-doc-type]').value = ex.doc_type || 'INV';
  overlay.querySelector('[data-unit]').value = ex.schedule_unit || 'month';
  overlay.querySelector('[data-action]').value = ex.action || 'draft';
  // Contact = searchSelect combobox (iT: contact pickers always search).
  const contactSel = searchSelect({
    options: contacts.map(c => ({ value: String(c.id), label: c.name, keywords: (c.code || '') + ' ' + (c.email || '') })),
    value: ex.contact_id != null ? String(ex.contact_id) : '',
    ariaLabel: t('recurring.contact', 'Contact'),
    placeholder: t('recurring.contact_ph', 'e.g. Acme Ltd') });
  overlay.querySelector('[data-contact-cell]').append(contactSel.el);

  function recompute(){
    let net = 0;
    [...linesBody.querySelectorAll('tr')].forEach(tr => {
      const qty = Number(tr.querySelector('[data-l-qty]').value) || 0;
      const price = Number(tr.querySelector('[data-l-price]').value) || 0;
      const disc = Number(tr.querySelector('[data-l-disc]').value) || 0;
      net += qty * price * (1 - disc / 100);
    });
    estEl.textContent = fmtR(Math.round(net * 100) / 100);
  }
  // recompute is referenced by lineRowNode's listeners (hoisted function declaration above).

  exLines.forEach(ln => linesBody.appendChild(lineRowNode(ln)));
  recompute();

  overlay.querySelector('[data-add-line]').addEventListener('click', () => { linesBody.appendChild(lineRowNode({ qty: 1, discount_pc: 0 })); recompute(); });

  const close = () => overlay.remove();
  overlay.querySelector('[data-cancel]').addEventListener('click', close);
  overlay.querySelector('[data-cancel2]').addEventListener('click', close);

  goEl.addEventListener('click', async () => {
    errEl.hidden = true;
    const fail = m => { errEl.hidden = false; errEl.textContent = m; };
    const reference2 = overlay.querySelector('[data-reference]').value.trim();
    if(!reference2) return fail(t('recurring.err_reference', 'A profile reference (name) is required'));
    const contactId = contactSel.get();
    if(!contactId) return fail(t('recurring.err_contact', 'Choose a contact'));
    const lines = [...linesBody.querySelectorAll('tr')].map(tr => ({
      description: tr.querySelector('[data-l-desc]').value.trim(),
      qty: Number(tr.querySelector('[data-l-qty]').value) || 0,
      unit_price: Number(tr.querySelector('[data-l-price]').value) || 0,
      discount_pc: Number(tr.querySelector('[data-l-disc]').value) || 0,
      account_id: (tr._acctSel && tr._acctSel.get()) || null,
      tax_code_id: (tr._taxSel && tr._taxSel.get()) || null,
    })).filter(l => l.description);
    if(!lines.length) return fail(t('recurring.err_lines', 'Add at least one line with a description'));
    const body = {
      reference: reference2,
      doc_type: overlay.querySelector('[data-doc-type]').value,
      contact_id: Number(contactId),
      schedule_every: Number(overlay.querySelector('[data-every]').value) || 1,
      schedule_unit: overlay.querySelector('[data-unit]').value,
      start_date: overlay.querySelector('[data-start]').value,
      end_date: overlay.querySelector('[data-end]').value || null,
      action: overlay.querySelector('[data-action]').value,
      notes: overlay.querySelector('[data-notes]').value.trim() || null,
      lines,
    };
    goEl.disabled = true; goEl.textContent = t('common.saving', 'Saving…');
    try {
      if(ex.id) await api.put('/companies/' + companyId + '/recurring-profiles/' + ex.id, body);
      else await api.post('/companies/' + companyId + '/recurring-profiles', body);
      close(); await onSaved();
      if(toast) toast(ex.id ? t('recurring.saved', 'Profile saved') : t('recurring.created', 'Profile created'));
    } catch(e){
      goEl.disabled = false; goEl.textContent = ex.id ? t('common.save', 'Save') : t('recurring.create', 'Create profile');
      fail((e.data && e.data.error) || t('recurring.save_failed', 'Could not save the profile'));
    }
  });

  document.body.appendChild(node);
}
